import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ClipboardList,
  Clock3,
  DollarSign,
  Eye,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Printer,
  RefreshCw,
  Search,
  ThumbsUp,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos, type AppPermisos } from '../../hooks/useAppPermisos';
import { logModuloEvento } from '../../utils/bitacora';
import { useEsSuperUsuario } from '../../hooks/useEsSuperUsuario';
import { useNavbarSubtitulo } from '../../context/NavbarSubtituloContext';
import { CierreSemanalFincaPage, generarReporteHorasFinca } from './CierreSemanalFincaPage';
import { useTableSort, sortRows, SortableTh } from '../../utils/tableSort';
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';
import { imprimirBoletaLabores } from './boletaLaboresImprimir';

type EstadoBoleta = 'borrador' | 'pendiente' | 'revisada' | 'aprobada' | 'rechazada' | 'anulada';
type ModalidadLabor = 'hora' | 'fijo' | 'tarea' | 'produccion';

interface CampoBoleta {
  id: string;
  numero: string;
  fecha: string;
  supervisor_nombre: string | null;
  finca_nombre: string | null;
  lote_num: string | null;
  estadio_codigo: string | null;
  estado: EstadoBoleta;
  costo_total: number | null;
  inicio_lat: number | null;
  inicio_lng: number | null;
  fin_lat: number | null;
  fin_lng: number | null;
  observacion?: string | null;
  creado_por?: string | null;
  revisado_por?: string | null;
  revisado_at?: string | null;
  aprobado_por?: string | null;
  aprobado_at?: string | null;
  cierre_semanal_id?: string | null;
  created_at: string;
}

interface Finca { id: string; nombre: string; codigo?: string | null }
interface Lote { id: string; finca_id: string; numero: string; nombre: string; area_ha?: number | null }
interface Bloque { id: string; lote_id: string; num: string; area_ha?: number | null; plant_count?: number | null; estadio_vfp?: string | null }
interface Labor {
  id: string;
  codigo_vfp: string;
  nombre: string;
  modalidad_default: ModalidadLabor;
  unidad_default: string;
  tarifa_base: number | null;
  requiere_bloque: boolean;
  requiere_estadio: boolean;
  requiere_gps: boolean;
  requiere_produccion: boolean;
}

interface Colaborador {
  id: number;
  nombre_completo: string;
  numero_empleado?: string | null;
  identificacion?: string | null;
  estado?: string | null;
  departamento_id?: number | null;
}

// Grupo contable de Planilla (Comercial, Costo - Finca, Costo - Planta Empacadora…).
// La boleta se captura para un solo grupo: solo se ofrecen sus colaboradores.
interface GrupoContable { id: number; codigo: string; nombre: string }
const grupoBoletaKey = (empresaId: number) => `boletas-labores-grupo-${empresaId}`;
// Aplicaciones es la app de la finca: solo captura personal de estos grupos.
// (Planta Empacadora y Comercial registran desde sus propias apps / el ERP.)
const GRUPOS_APLICACIONES = ['costo_finca'];

interface Cuadrilla { id: string; codigo: string; nombre: string; supervisor_nombre?: string | null }
interface CuadrillaIntegrante {
  cuadrilla_id: string;
  trabajador_codigo: string | null;
  trabajador_nombre: string;
  vigente_desde: string;
  vigente_hasta: string | null;
}

interface TrabajadorLinea {
  key: string;
  nombre: string;
  codigo: string;
  laborId: string;
  laborTexto: string;
  // Lote donde trabajó ESTE colaborador (la labor aplica a todos sus bloques).
  loteId: string;
  horas: string;
  produccion: string;
  horaInicio: string;
  horaFin: string;
  excepcion: string;
  // Jornada ordinaria vigente al AGREGAR la fila: cambiar la del encabezado despues
  // no altera a los colaboradores ya incluidos. 0 = aun no agregada (usa la actual).
  jornada: number;
}

// Jornada ordinaria por defecto CR: 8 h. Es editable por boleta; lo que exceda es extra.
const JORNADA_ORDINARIA_DEFAULT = 8;
// Recargo legal minimo de horas extra en CR (tiempo y medio). Solo aplica a
// labores por hora: fijo/produccion/tarea no tienen "jornada" en ese sentido.
const FACTOR_HORA_EXTRA = 1.5;

const MOTIVO_LABEL: Record<string, string> = {
  entrada_tardia: 'Entrada tardía',
  salida_temprano: 'Salida temprano',
  permiso: 'Permiso',
  incapacidad: 'Incapacidad',
  jornada_parcial: 'Jornada parcial',
  ausente: 'Ausente',
  vacaciones: 'Vacaciones',
  otro: 'Otro',
};
// Asistencias sin marcas de entrada/salida. Vacaciones sí reconocen 8 h pagadas.
const MOTIVOS_SIN_HORAS = ['ausente', 'incapacidad', 'permiso', 'vacaciones'];
const OPCIONES_MOTIVO: { v: string; l: string }[] = [
  { v: '', l: 'Normal' },
  { v: 'entrada_tardia', l: 'Entrada tardía' },
  { v: 'salida_temprano', l: 'Salida temprano' },
  { v: 'jornada_parcial', l: 'Jornada parcial' },
  { v: 'permiso', l: 'Permiso' },
  { v: 'incapacidad', l: 'Incapacidad' },
  { v: 'ausente', l: 'Ausente' },
  { v: 'vacaciones', l: 'Vacaciones' },
  { v: 'otro', l: 'Otro' },
];

function nuevaLinea(): TrabajadorLinea {
  return {
    key: crypto.randomUUID(),
    nombre: '',
    codigo: '',
    laborId: '',
    laborTexto: '',
    loteId: '',
    horas: '',
    produccion: '',
    horaInicio: '',
    horaFin: '',
    excepcion: '',
    jornada: 0,
  };
}

const ESTADO_LABEL: Record<EstadoBoleta, string> = {
  borrador: 'Borrador',
  pendiente: 'Pendiente',
  revisada: 'Revisada',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  anulada: 'Anulada',
};

const ESTADO_COLOR: Record<EstadoBoleta, string> = {
  borrador: 'var(--ink-faint)',
  pendiente: 'var(--tone-info-text)',
  revisada: 'var(--tone-purple-text)',
  aprobada: 'var(--tone-success-text)',
  rechazada: 'var(--tone-warn-text)',
  anulada: '#f87171',
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Semana de planilla de finca: sábado a viernes. Devuelve el sábado que la inicia.
function sabadoDeSemana(fecha: string) {
  const d = new Date(`${fecha}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() - 6 + 7) % 7));
  return d.toISOString().slice(0, 10);
}
const DIAS_SEM = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const diaSem = (s: string) => DIAS_SEM[new Date(`${s}T12:00:00Z`).getUTCDay()];

function fmtCRC(v?: number | null) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return 'CRC ' + n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(`${s}T00:00:00`).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function weekLabel(fecha: string) {
  const d = new Date(`${fecha}T00:00:00`);
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${String(week).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
}

function buildDateTime(fecha: string, hora: string) {
  if (!fecha || !hora) return null;
  return new Date(`${fecha}T${hora}:00`).toISOString();
}

function horasDesdeLinea(t: Pick<TrabajadorLinea, 'horas' | 'horaInicio' | 'horaFin' | 'excepcion'>) {
  if (t.excepcion === 'vacaciones') return 8;
  const manual = Number(t.horas || 0);
  if (manual > 0) return manual;
  if (!t.horaInicio || !t.horaFin) return 0;
  const [hi, mi] = t.horaInicio.split(':').map(Number);
  const [hf, mf] = t.horaFin.split(':').map(Number);
  if (![hi, mi, hf, mf].every(Number.isFinite)) return 0;
  let inicio = hi * 60 + mi;
  let fin = hf * 60 + mf;
  if (fin < inicio) fin += 24 * 60;
  return Math.max(0, (fin - inicio) / 60);
}

// Separa el total de horas en ordinarias (hasta la jornada) y extra (el resto).
function horasOrdinarias(total: number, jornada: number) {
  return Math.min(total, jornada);
}
function horasExtra(total: number, jornada: number) {
  return Math.max(0, total - jornada);
}

// Monto por labor "hora": ordinarias a tarifa normal + extra con recargo legal.
function montoPorHoras(ordinarias: number, extra: number, tarifa: number) {
  return ordinarias * tarifa + extra * tarifa * FACTOR_HORA_EXTRA;
}

// Horas decimales → "H:MM" (ej. 1.0833 → "1:05"). Para leer jornadas sin confundir
// los minutos con decimales. Lo que se guarda en BD sigue siendo decimal (preciso).
function fmtHM(n: number) {
  if (!Number.isFinite(n) || n <= 0) return '0:00';
  const totalMin = Math.round(n * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

// "HH:MM" + horas decimales → "HH:MM" (envuelve a 24h).
function sumarHoras(hora: string, horas: number) {
  const [h, m] = hora.split(':').map(Number);
  if (![h, m].every(Number.isFinite)) return hora;
  let mins = (h * 60 + m + Math.round(horas * 60)) % (24 * 60);
  if (mins < 0) mins += 24 * 60;
  const hh = String(Math.floor(mins / 60)).padStart(2, '0');
  const mm = String(mins % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function asistenciaDesdeExcepcion(excepcion: string) {
  if (excepcion === 'permiso') return 'permiso';
  if (excepcion === 'incapacidad') return 'incapacidad';
  if (excepcion === 'entrada_tardia') return 'tardia';
  if (excepcion === 'ausente') return 'ausente';
  if (excepcion === 'vacaciones') return 'vacaciones';
  return 'presente';
}


interface OpcionSelector {
  id: string;
  codigo: string;
  titulo: string;
  subtitulo?: string;
  requiereGps?: boolean;
}

// Modal de selección con búsqueda (createPortal a document.body — regla MYA).
function SelectorModal({
  titulo,
  placeholder,
  opciones,
  onSelect,
  onClose,
  inicial,
  accion,
}: {
  titulo: string;
  placeholder: string;
  opciones: OpcionSelector[];
  onSelect: (o: OpcionSelector) => void;
  onClose: () => void;
  inicial?: string;
  // Botón opcional al pie (ej. registrar un colaborador que no aparece).
  accion?: { label: string; onClick: (texto: string) => void };
}) {
  const [q, setQ] = useState(inicial || '');
  // Navegación con teclado: ↑/↓ mueven la selección, Enter elige.
  const [activo, setActivo] = useState(0);
  const listaRef = useRef<HTMLDivElement>(null);
  const filtradas = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return opciones.slice(0, 200);
    return opciones
      .filter((o) => {
        const hay = `${o.codigo} ${o.titulo} ${o.subtitulo || ''}`.toLowerCase();
        return terms.every((t) => hay.includes(t));
      })
      .slice(0, 200);
  }, [q, opciones]);

  useEffect(() => { setActivo(0); }, [q]);
  useEffect(() => {
    listaRef.current?.querySelector<HTMLElement>(`[data-idx="${activo}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activo]);
  function onTeclas(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActivo((i) => Math.min(i + 1, filtradas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); const o = filtradas[activo]; if (o) onSelect(o); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center p-4 sm:p-8"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onMouseDown={onClose}
    >
      <div
        className="mt-10 flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border shadow-2xl"
        style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
          <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>{titulo}</div>
          <button type="button" className="rounded p-1" style={{ color: 'var(--ink-faint)' }} onClick={onClose} title="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="border-b p-3" style={{ borderColor: 'var(--line)' }}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5" size={15} style={{ color: 'var(--ink-faint)' }} />
            <input
              autoFocus
              className={`${inputCls} pl-9`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onTeclas}
              placeholder={placeholder}
              spellCheck={false}
            />
          </div>
        </div>
        <div ref={listaRef} className="flex-1 overflow-auto p-1">
          {filtradas.length ? filtradas.map((o, idx) => (
            <button
              key={o.id}
              type="button"
              data-idx={idx}
              className="flex w-full items-center gap-3 rounded px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-overlay)]"
              style={{ color: 'var(--ink)', background: idx === activo ? 'var(--surface-overlay)' : undefined, outline: idx === activo ? '1px solid var(--accent)' : undefined }}
              onMouseEnter={() => setActivo(idx)}
              onClick={() => onSelect(o)}
            >
              <span className="font-mono font-semibold" style={{ color: 'var(--accent)', minWidth: 44 }}>{o.codigo}</span>
              <span className="flex flex-col">
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  {o.titulo}
                </span>
                {o.subtitulo && <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{o.subtitulo}</span>}
              </span>
            </button>
          )) : (
            <div className="px-3 py-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>Sin coincidencias.</div>
          )}
        </div>
        {accion && (
          <div className="border-t p-3" style={{ borderColor: 'var(--line)' }}>
            <button type="button" className={`${btnSecondary} flex w-full items-center justify-center gap-2`} onClick={() => accion.onClick(q.trim())}>
              <Plus size={14} /> {accion.label}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Alta rápida de un colaborador desde la boleta. Crea la ficha en Planilla con
// lo mínimo (nombre, cédula, ingreso, departamento del grupo de la boleta) y
// salario en 0: queda con expediente pendiente, así NO entra a la planilla
// hasta que RRHH complete salario y datos legales en el ERP.
interface DepartamentoPl { id: number; nombre: string; grupo_contable_id: number | null; activo?: boolean | null }

function AltaRapidaColaborador({
  empresaId,
  grupoNombre,
  departamentos,
  registradoPor,
  textoInicial,
  onCreado,
  onClose,
}: {
  empresaId: number;
  grupoNombre: string;
  departamentos: DepartamentoPl[];
  registradoPor: string;
  textoInicial: string;
  onCreado: (c: Colaborador) => void;
  onClose: () => void;
}) {
  const soloDigitos = /^[\d\s-]+$/.test(textoInicial);
  const [nombre, setNombre] = useState('');
  const [ap1, setAp1] = useState('');
  const [ap2, setAp2] = useState('');
  const [tipoId, setTipoId] = useState<'cedula' | 'dimex' | 'pasaporte'>('cedula');
  const [identificacion, setIdentificacion] = useState(soloDigitos ? textoInicial.replace(/\D/g, '') : '');
  const [fechaIngreso, setFechaIngreso] = useState(today());
  const [deptoId, setDeptoId] = useState(departamentos.length === 1 ? String(departamentos[0].id) : '');
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');
  const nombreCompleto = [nombre, ap1, ap2].map((x) => x.trim()).filter(Boolean).join(' ').toUpperCase();

  async function siguienteCodigo(): Promise<string> {
    const { data } = await supabase.from('pl_colaboradores').select('numero_empleado').eq('empresa_id', empresaId).range(0, 4999);
    const max = (data ?? []).reduce((m: number, r: { numero_empleado: string | null }) => {
      const t = String(r.numero_empleado || '').trim();
      const n = /^\d+$/.test(t) ? Number(t) : 0;
      return n > m ? n : m;
    }, 0);
    return String(max + 1).padStart(3, '0');
  }

  async function guardar() {
    setErr('');
    const ident = identificacion.replace(/[\s-]/g, '').trim();
    if (!nombre.trim() || !ap1.trim()) { setErr('Indique el nombre y el primer apellido.'); return; }
    if (!ident) { setErr('La identificación es obligatoria para evitar fichas duplicadas.'); return; }
    if (tipoId === 'cedula' && !/^\d{9}$/.test(ident)) { setErr('La cédula debe tener 9 dígitos (ej. 205010571).'); return; }
    if (!fechaIngreso) { setErr('Indique la fecha de ingreso.'); return; }
    if (!deptoId) { setErr('Seleccione el departamento.'); return; }
    setGuardando(true);
    try {
      // ¿Ya existe esa identificación? Decir en qué ficha, no duplicar.
      const { data: existe } = await supabase.from('pl_colaboradores')
        .select('numero_empleado, nombre_completo, estado').eq('empresa_id', empresaId).eq('identificacion', ident).neq('estado', 'bloqueado').limit(1).maybeSingle();
      if (existe) {
        setErr(existe.estado === 'activo'
          ? `${ident} ya está registrada en ${existe.nombre_completo} (código ${existe.numero_empleado}). Búsquelo en la lista: puede estar en otro grupo contable.`
          : `${ident} ya está registrada en ${existe.nombre_completo} (código ${existe.numero_empleado}), que está ${existe.estado}. Pida a RRHH que lo recontrate en Planilla.`);
        return;
      }
      const hoy = new Date().toLocaleDateString('es-CR');
      for (let intento = 0; intento < 3; intento++) {
        const codigo = await siguienteCodigo();
        const { data, error } = await supabase.from('pl_colaboradores').insert({
          empresa_id: empresaId,
          numero_empleado: codigo,
          modalidad_planilla: 'finca',
          tipo_identificacion: tipoId,
          identificacion: ident,
          nombre_completo: nombreCompleto,
          nombre: nombre.trim().toUpperCase(),
          primer_apellido: ap1.trim().toUpperCase(),
          segundo_apellido: ap2.trim().toUpperCase() || null,
          fecha_ingreso: fechaIngreso,
          departamento_id: Number(deptoId),
          tipo_salario: 'hora',
          salario: 0,
          estado: 'activo',
          notas: `Alta rápida desde Boletas de Labores por ${registradoPor} el ${hoy}. Falta completar salario y datos legales en Planilla.`,
        }).select('id, nombre_completo, numero_empleado, identificacion, estado, departamento_id').single();
        if (!error && data) {
          logModuloEvento({ empresaId, modulo: 'campo_labores', accion: 'crear', entidad: 'pl_colaboradores', entidadId: String(data.id), descripcion: `Alta rápida de colaborador ${codigo} ${nombreCompleto}` });
          onCreado(data as Colaborador);
          return;
        }
        // Otro usuario tomó el mismo código al mismo tiempo: probar con el siguiente.
        if (error?.code === '23505' && error.message.includes('numero_empleado')) continue;
        setErr(error?.code === '23505' ? 'Ya existe un colaborador con esa identificación.' : 'No se pudo registrar el colaborador. Intente de nuevo.');
        return;
      }
      setErr('No se pudo asignar un código de empleado. Intente de nuevo.');
    } finally {
      setGuardando(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-start justify-center p-4 sm:p-8" style={{ background: 'rgba(0,0,0,0.55)' }} onMouseDown={onClose}>
      <div className="mt-10 w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
          <div>
            <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Registrar colaborador nuevo</div>
            <div className="text-xs" style={{ color: 'var(--ink-faint)' }}>Grupo contable: {grupoNombre}</div>
          </div>
          <button type="button" className="rounded p-1" style={{ color: 'var(--ink-faint)' }} onClick={onClose} title="Cerrar"><X size={18} /></button>
        </div>
        <div className="space-y-3 p-4">
          {err && (
            <div className="rounded border px-3 py-2 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>{err}</div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><label className={labelCls}>Nombre(s) *</label><input autoFocus className={inputCls} value={nombre} onChange={(e) => setNombre(e.target.value)} /></div>
            <div><label className={labelCls}>1er apellido *</label><input className={inputCls} value={ap1} onChange={(e) => setAp1(e.target.value)} /></div>
            <div><label className={labelCls}>2do apellido</label><input className={inputCls} value={ap2} onChange={(e) => setAp2(e.target.value)} /></div>
          </div>
          <div>
            <label className={labelCls}>Nombre completo</label>
            <div className="rounded border px-3 py-2 text-sm font-semibold" style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)', color: nombreCompleto ? 'var(--ink)' : 'var(--ink-faint)' }}>
              {nombreCompleto || 'Se completa con el nombre y los apellidos'}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Tipo de identificación</label>
              <select className={selectCls} value={tipoId} onChange={(e) => setTipoId(e.target.value as 'cedula' | 'dimex' | 'pasaporte')}>
                <option value="cedula">Cédula</option>
                <option value="dimex">DIMEX</option>
                <option value="pasaporte">Pasaporte</option>
              </select>
            </div>
            <div><label className={labelCls}>Identificación *</label><input className={inputCls} value={identificacion} onChange={(e) => setIdentificacion(e.target.value)} placeholder={tipoId === 'cedula' ? '9 dígitos' : ''} /></div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className={labelCls}>Fecha de ingreso *</label><input type="date" className={inputCls} value={fechaIngreso} onChange={(e) => setFechaIngreso(e.target.value)} /></div>
            <div>
              <label className={labelCls}>Departamento *</label>
              <select className={selectCls} value={deptoId} onChange={(e) => setDeptoId(e.target.value)}>
                <option value="">Seleccione</option>
                {departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
            Queda con expediente pendiente: ya se puede incluir en boletas, pero entra a la planilla hasta que RRHH complete su salario en Planilla.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--line)' }}>
          <button type="button" className={btnSecondary} onClick={onClose} disabled={guardando}>Cancelar</button>
          <button type="button" className={`${btnPrimary} flex items-center gap-2`} onClick={guardar} disabled={guardando}>
            {guardando && <Loader2 size={14} className="animate-spin" />} Registrar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function EstadoBadge({ estado }: { estado: EstadoBoleta }) {
  return (
    <span
      className="inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{
        color: ESTADO_COLOR[estado],
        background: 'var(--surface-overlay)',
        border: '1px solid var(--line)',
      }}
    >
      {ESTADO_LABEL[estado]}
    </span>
  );
}

function Kpi({ label, value, icon, tone }: { label: string; value: string | number; icon: React.ReactNode; tone: string }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

function BoletaForm({
  empresaId,
  supervisorNombre,
  fincas,
  lotes,
  bloques,
  labores,
  colaboradores,
  grupos,
  grupoDeColab,
  departamentos,
  onColaboradorCreado,
  cuadrillas,
  integrantesCuadrilla,
  onClose,
  onSaved,
  onError,
  boletaId,
}: {
  boletaId?: string;   // con valor: editar una boleta abierta; sin valor: nueva
  empresaId: number;
  supervisorNombre: string;
  fincas: Finca[];
  lotes: Lote[];
  bloques: Bloque[];
  labores: Labor[];
  colaboradores: Colaborador[];
  grupos: GrupoContable[];
  grupoDeColab: (c: Colaborador) => number | null;
  departamentos: DepartamentoPl[];
  onColaboradorCreado: (c: Colaborador) => void;
  cuadrillas: Cuadrilla[];
  integrantesCuadrilla: CuadrillaIntegrante[];
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [fecha, setFecha] = useState(today());
  // Grupo contable elegido antes de capturar; se recuerda hasta que se cambie.
  const [grupoId, setGrupoId] = useState<string>(() => {
    try { return localStorage.getItem(grupoBoletaKey(empresaId)) || ''; } catch { return ''; }
  });
  const [jornadaOrdinaria, setJornadaOrdinaria] = useState(String(JORNADA_ORDINARIA_DEFAULT));
  // Horario estándar del día: pre-llena la HI/HF de cada empleado nuevo.
  const [entradaDia, setEntradaDia] = useState('');
  const [salidaDia, setSalidaDia] = useState('');
  const [observacion, setObservacion] = useState('');
  // Filas YA confirmadas (tabla solo lectura).
  const [trabajadores, setTrabajadores] = useState<TrabajadorLinea[]>([]);
  // Fila en captura (formulario). La labor se conserva entre capturas (lo común).
  const [captura, setCaptura] = useState<TrabajadorLinea>(nuevaLinea());
  const [modalEmpleado, setModalEmpleado] = useState(false);
  // Alta rápida: texto que se venía buscando (para prellenar la cédula); null = cerrado.
  const [altaTexto, setAltaTexto] = useState<string | null>(null);
  const [modalLabor, setModalLabor] = useState(false);
  // Texto del campo Labor: se digita el codigo; si no existe, Enter abre la busqueda con ese texto.
  const [laborDigitada, setLaborDigitada] = useState('');
  const [modalCuadrilla, setModalCuadrilla] = useState(false);
  const [saving, setSaving] = useState(false);
  // Edición: número de la boleta que se está corrigiendo y si ya terminó de cargar.
  const [numeroEditando, setNumeroEditando] = useState('');
  const [cargandoBoleta, setCargandoBoleta] = useState(!!boletaId);
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  // Fila cuya labor se está buscando en el modal (null = la labor de la fila de captura).
  const [laborFila, setLaborFila] = useState<{ key: string; texto: string } | null>(null);
  // Tarifa/hora REAL de cada colaborador (por su salario en Planilla), para
  // labores de modalidad "hora". Se recalcula cuando cambia la fecha o la
  // lista de colaboradores en la boleta.
  const [tarifasColaborador, setTarifasColaborador] = useState<Record<string, number>>({});

  const grupoValido = grupos.some((g) => String(g.id) === grupoId);
  // F3 abre el buscador de empleados (en lugar de la búsqueda del navegador).
  useEffect(() => {
    const onF3 = (e: KeyboardEvent) => {
      if (e.key !== 'F3') return;
      e.preventDefault();
      if (grupoValido) setModalEmpleado(true);
      else onError('Seleccione primero el grupo contable de la boleta.');
    };
    window.addEventListener('keydown', onF3);
    return () => window.removeEventListener('keydown', onF3);
  }, [grupoValido, onError]);
  const grupoUnico = grupos.length === 1 ? String(grupos[0].id) : '';
  useEffect(() => {
    if (grupoUnico && grupoId !== grupoUnico && trabajadores.length === 0) setGrupoId(grupoUnico);
  }, [grupoUnico, grupoId, trabajadores.length]);
  const colaboradoresGrupo = useMemo(
    () => (grupoValido ? colaboradores.filter((c) => String(grupoDeColab(c)) === grupoId) : []),
    [colaboradores, grupoDeColab, grupoId, grupoValido],
  );
  const comercialId = grupos.find((g) => g.codigo === 'comercial')?.id ?? null;
  const departamentosGrupo = departamentos.filter((d) => d.activo !== false && String(d.grupo_contable_id ?? comercialId) === grupoId);
  const grupoNombre = grupos.find((g) => String(g.id) === grupoId)?.nombre ?? '';

  function cambiarGrupo(id: string) {
    if (trabajadores.length > 0) {
      onError('La boleta ya tiene colaboradores de otro grupo contable. Quítelos antes de cambiar de grupo.');
      return;
    }
    setGrupoId(id);
    setCaptura(nuevaLinea());
    try { localStorage.setItem(grupoBoletaKey(empresaId), id); } catch { /* sin almacenamiento local */ }
  }

  // El lote va en cada colaborador (la labor aplica a todos los bloques del lote).
  const loteDe = useCallback((id: string) => lotes.find((l) => l.id === id), [lotes]);
  const etiquetaLote = (l: Lote) => `${l.numero} · ${fincas.find((f) => f.id === l.finca_id)?.nombre ?? ''}`;
  // Estadio de un lote: el más frecuente entre sus bloques activos.
  const estadioDeLote = useCallback((id: string) => {
    const cuenta: Record<string, number> = {};
    for (const b of bloques) if (b.lote_id === id && b.estadio_vfp) cuenta[b.estadio_vfp] = (cuenta[b.estadio_vfp] || 0) + 1;
    return Object.entries(cuenta).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  }, [bloques]);
  const jornadaNum = Math.max(0, Number(jornadaOrdinaria || 0)) || JORNADA_ORDINARIA_DEFAULT;
  // Jornada de una fila ya agregada (la que regia al incluirla); si no tiene, la actual.
  const jornadaDe = (t: TrabajadorLinea) => t.excepcion === 'vacaciones' ? 8 : t.jornada || jornadaNum;
  const lineasValidas = trabajadores;
  const personas = lineasValidas.length;
  const totalHorasTrabajadores = lineasValidas.reduce((s, t) => s + horasDesdeLinea(t), 0);
  // La labor (y por ende la tarifa) es por colaborador: el costo total se suma fila a fila.
  const laborDe = useCallback(
    (id: string) => labores.find((l) => l.id === id),
    [labores],
  );
  // Tarifa/hora a usar para una linea: si la labor es "hora", el salario real
  // del colaborador manda (con la tarifa de la labor solo como respaldo si el
  // colaborador no tiene registro en Planilla). Fijo/produccion/tarea siguen
  // usando la tarifa de la labor -- no son un salario por hora.
  const tarifaHoraDe = useCallback((t: TrabajadorLinea, lab: Labor | undefined) => {
    if (t.excepcion === 'vacaciones') return tarifasColaborador[t.codigo] ?? 0;
    if (lab?.modalidad_default === 'hora') {
      const real = tarifasColaborador[t.codigo];
      if (real !== undefined && real !== null) return real;
    }
    return lab?.tarifa_base ?? 0;
  }, [tarifasColaborador]);

  // Recarga las tarifas reales cuando cambian los colaboradores en la boleta o la fecha.
  useEffect(() => {
    const codigos = Array.from(new Set(
      [...trabajadores, captura].map((t) => t.codigo.trim()).filter(Boolean),
    ));
    if (codigos.length === 0 || !fecha) { setTarifasColaborador({}); return; }
    let cancelado = false;
    (async () => {
      const { data, error } = await supabase.rpc('app_ab_tarifas_hora_colaboradores', {
        p_empresa_id: empresaId, p_codigos: codigos, p_fecha: fecha,
      });
      if (cancelado || error || !data) return;
      const map: Record<string, number> = {};
      for (const row of data as { codigo: string; tarifa_hora: number | null }[]) {
        if (row.tarifa_hora !== null) map[row.codigo] = Number(row.tarifa_hora);
      }
      setTarifasColaborador(map);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, fecha, trabajadores.map((t) => t.codigo).join(','), captura.codigo]);

  const costo = lineasValidas.reduce((s, t) => {
    const lab = laborDe(t.laborId);
    const tarifaLinea = tarifaHoraDe(t, lab);
    const horasLinea = horasDesdeLinea(t);
    if (lab?.modalidad_default === 'fijo') return s + tarifaLinea;
    if (lab?.modalidad_default === 'produccion' || lab?.modalidad_default === 'tarea') {
      return s + Number(t.produccion || 0) * tarifaLinea;
    }
    return s + montoPorHoras(horasOrdinarias(horasLinea, jornadaDe(t)), horasExtra(horasLinea, jornadaDe(t)), tarifaLinea);
  }, 0);

  // Define el horario estándar del día y lo refleja en la captura actual.
  function setEntradaDiaSync(value: string) {
    setEntradaDia(value);
    const salida = value ? sumarHoras(value, jornadaNum) : '';
    if (value) setSalidaDia(salida);
    setCaptura((prev) => ({ ...prev, horaInicio: value, horaFin: value ? (salidaDia || salida) : '' }));
  }
  function setSalidaDiaSync(value: string) {
    setSalidaDia(value);
    setCaptura((prev) => ({ ...prev, horaFin: value }));
  }

  // ── Captura de un colaborador (fila de formulario) ──
  function editarCaptura(field: keyof Omit<TrabajadorLinea, 'key'>, value: string) {
    setCaptura((prev) => {
      const next = { ...prev, [field]: value };
      // Asistencias sin marcas; vacaciones se reconocen con 8 h pagadas.
      if (field === 'excepcion' && MOTIVOS_SIN_HORAS.includes(value)) {
        next.horaInicio = ''; next.horaFin = ''; next.horas = '';
        if (value === 'vacaciones') { next.laborId = ''; next.laborTexto = ''; next.loteId = ''; }
      }
      // Al fijar la hora de inicio, pre-establecer la final = inicio + jornada ordinaria.
      // El usuario puede variarla después.
      if (field === 'horaInicio' && value) {
        next.horaFin = sumarHoras(value, jornadaNum);
      }
      return next;
    });
  }

  function elegirEmpleadoCaptura(c: Colaborador) {
    setCaptura((prev) => ({
      ...prev,
      nombre: c.nombre_completo,
      codigo: c.numero_empleado || c.identificacion || String(c.id),
    }));
    setModalEmpleado(false);
    enfocarNav(captura.laborId ? 'hi' : 'labor');
  }

  function elegirLaborCaptura(l: Labor) {
    setCaptura((prev) => ({ ...prev, laborId: l.id, laborTexto: `${l.codigo_vfp} - ${l.nombre}` }));
    setModalLabor(false);
    enfocarNav('hi');
  }

  // Enter avanza al siguiente campo del formulario (en orden de pantalla). En los
  // botones (Empleado, Labor, Agregar) Enter conserva su accion normal (clic).
  function enfocarNav(nombre: string) {
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-nav="${nombre}"]`);
      el?.focus();
      if (el instanceof HTMLInputElement) el.select?.();
    });
  }
  function onEnterAvanza(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter') return;
    const el = e.target as HTMLElement;
    if (!el.dataset?.nav || el.tagName === 'BUTTON' || el.tagName === 'TEXTAREA') return;
    e.preventDefault();
    const campos = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-nav]'))
      .filter((c) => !(c as HTMLInputElement).disabled && c.offsetParent !== null);
    const i = campos.indexOf(el);
    const sig = campos[i + 1];
    if (sig) {
      sig.focus();
      if (sig instanceof HTMLInputElement) sig.select?.();
    }
  }

  // Mantiene el campo Labor sincronizado con la labor elegida (al elegir, al agregar y al editar una fila).
  useEffect(() => { setLaborDigitada(captura.laborTexto || ''); }, [captura.laborId, captura.laborTexto]);

  // Busca una labor por su codigo exacto (sin distinguir mayusculas ni ceros a la izquierda).
  function laborPorCodigo(texto: string) {
    const t = texto.trim().toLowerCase();
    if (!t) return undefined;
    const sinCeros = (v: string) => v.replace(/^0+(?=\d)/, '');
    return labores.find((l) => {
      const c = String(l.codigo_vfp || '').trim().toLowerCase();
      return c === t || sinCeros(c) === sinCeros(t);
    });
  }
  function onLaborKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation(); // este campo maneja su propio Enter
    const t = laborDigitada.trim();
    if (captura.laborId && t === captura.laborTexto) { enfocarNav('hi'); return; }
    const l = laborPorCodigo(t);
    if (l) { elegirLaborCaptura(l); return; }
    setModalLabor(true); // no existe ese codigo (o se escribio un nombre): abrir busqueda con el texto
  }
  function onLaborBlur() {
    const t = laborDigitada.trim();
    if (captura.laborId && t === captura.laborTexto) return;
    const l = laborPorCodigo(t);
    if (l) setCaptura((prev) => ({ ...prev, laborId: l.id, laborTexto: `${l.codigo_vfp} - ${l.nombre}` }));
  }

  function agregarCaptura() {
    if (!captura.nombre.trim()) { onError('Seleccione el colaborador.'); return; }
    if (!captura.laborId && !MOTIVOS_SIN_HORAS.includes(captura.excepcion)) { onError('Seleccione la labor.'); return; }
    if (!captura.loteId && !MOTIVOS_SIN_HORAS.includes(captura.excepcion)) { onError('Seleccione el lote.'); return; }
    if (trabajadores.some((t) => t.codigo && t.codigo === captura.codigo)) {
      onError(`${captura.nombre.trim()} ya está en la lista.`);
      return;
    }
    setTrabajadores((prev) => [...prev, { ...captura, key: crypto.randomUUID(), jornada: captura.excepcion === 'vacaciones' ? 8 : captura.jornada || jornadaNum }]);
    // Limpia el colaborador pero conserva labor y horario del día (lo común: todos igual).
    setCaptura((prev) => ({
      ...nuevaLinea(),
      laborId: prev.laborId,
      laborTexto: prev.laborTexto,
      loteId: prev.loteId,
      horaInicio: entradaDia,
      horaFin: salidaDia || (entradaDia ? sumarHoras(entradaDia, jornadaNum) : ''),
    }));
    enfocarNav('empleado');
  }

  // Agrega de una sola vez a todos los integrantes vigentes de una cuadrilla,
  // con la labor y el horario del dia ya definidos en la captura actual.
  function agregarCuadrilla(c: Cuadrilla) {
    if (!captura.laborId) { onError('Seleccione primero la labor para la cuadrilla.'); return; }
    const codigosGrupo = new Set(colaboradoresGrupo.map((x) => (x.numero_empleado || '').trim()).filter(Boolean));
    const integrantes = integrantesCuadrilla.filter((i) => i.cuadrilla_id === c.id && codigosGrupo.has((i.trabajador_codigo || '').trim()));
    if (integrantes.length === 0) { onError(`La cuadrilla ${c.nombre} no tiene integrantes vigentes en el grupo contable elegido.`); return; }
    const yaAgregados = new Set(trabajadores.map((t) => t.codigo));
    const nuevas = integrantes
      .filter((i) => !(i.trabajador_codigo && yaAgregados.has(i.trabajador_codigo)))
      .map((i) => ({
        ...nuevaLinea(),
        nombre: i.trabajador_nombre,
        codigo: i.trabajador_codigo || '',
        laborId: captura.laborId,
        laborTexto: captura.laborTexto,
        loteId: captura.loteId,
        horaInicio: entradaDia,
        horaFin: salidaDia || (entradaDia ? sumarHoras(entradaDia, jornadaNum) : ''),
        jornada: jornadaNum,
      }));
    if (nuevas.length === 0) { onError('Todos los integrantes de esa cuadrilla ya estan en la lista.'); return; }
    setTrabajadores((prev) => [...prev, ...nuevas]);
    setModalCuadrilla(false);
  }

  // Agrega de una vez a todo el personal activo del grupo (Costo - Finca) que
  // aún no esté en la boleta, con la labor (si ya hay una) y el horario de la
  // fila de captura.
  function agregarTodoPersonal() {
    if (!grupoValido) { onError('Seleccione primero el grupo contable de la boleta.'); return; }
    // La labor es opcional aquí: si no hay, se asigna luego fila por fila (al guardar se exige).
    const yaAgregados = new Set(trabajadores.map((t) => t.codigo));
    const nuevas = colaboradoresGrupo
      .filter((c) => c.estado === 'activo' && !yaAgregados.has(c.numero_empleado || c.identificacion || String(c.id)))
      .map((c) => ({
        ...nuevaLinea(),
        nombre: c.nombre_completo,
        codigo: c.numero_empleado || c.identificacion || String(c.id),
        laborId: captura.laborId,
        laborTexto: captura.laborTexto,
        loteId: captura.loteId,
        horaInicio: entradaDia,
        horaFin: salidaDia || (entradaDia ? sumarHoras(entradaDia, jornadaNum) : ''),
        jornada: jornadaNum,
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    if (nuevas.length === 0) { onError('Todo el personal activo del grupo ya está en la boleta.'); return; }
    setTrabajadores((prev) => [...prev, ...nuevas]);
  }

  function cambiarAsistencia(key: string, excepcion: string) {
    setTrabajadores((prev) => prev.map((t) => (t.key !== key ? t
      : MOTIVOS_SIN_HORAS.includes(excepcion)
        ? { ...t, excepcion, horaInicio: '', horaFin: '', horas: '',
            ...(excepcion === 'vacaciones' ? { laborId: '', laborTexto: '', loteId: '', jornada: 8 } : {}) }
        : { ...t, excepcion })));
  }

  function quitarTrabajador(key: string) {
    setTrabajadores((prev) => prev.filter((t) => t.key !== key));
  }

  // Devuelve la fila a la captura para corregirla; la saca de la tabla
  // (al volver a "Agregar" entra corregida, sin duplicar).
  // Edición directa en la fila (labor, asistencia, HI / HF), sin abrir nada.
  function setFila(key: string, patch: Partial<TrabajadorLinea>) {
    setTrabajadores((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  }
  function laborEnFila(key: string, texto: string, abrirSiNoExiste: boolean) {
    const t = texto.trim();
    if (!t) return;
    const l = laborPorCodigo(t);
    if (l) { setFila(key, { laborId: l.id, laborTexto: `${l.codigo_vfp} - ${l.nombre}` }); return; }
    if (abrirSiNoExiste) { setLaborFila({ key, texto: t }); setModalLabor(true); }
  }
  function horaEnFila(t: TrabajadorLinea, campo: 'horaInicio' | 'horaFin', valor: string) {
    const patch: Partial<TrabajadorLinea> = { [campo]: valor, horas: '' };
    // Al poner la entrada sin salida, la salida sugerida es entrada + jornada.
    if (campo === 'horaInicio' && valor && !t.horaFin) patch.horaFin = sumarHoras(valor, jornadaDe(t));
    setFila(t.key, patch);
  }


  useEffect(() => {
    if (!boletaId) return;
    let activo = true;
    (async () => {
      const [{ data: b }, { data: tr }] = await Promise.all([
        supabase.from('campo_boletas').select('numero, fecha, lote_id, observacion').eq('id', boletaId).eq('empresa_id', empresaId).single(),
      supabase.from('campo_boleta_trabajadores')
          .select('trabajador_codigo, trabajador_nombre, labor_id, lote_id, entrada_at, salida_at, horas_ordinarias, horas_extra, jornada_ordinaria_horas, cantidad_produccion, estado_asistencia, observacion')
          .eq('boleta_id', boletaId).eq('empresa_id', empresaId).order('trabajador_nombre'),
      ]);
      if (!activo) return;
      if (!b) { onError('No se pudo cargar la boleta.'); setCargandoBoleta(false); return; }
      const hhmm = (ts: string | null) => (ts
        ? new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Costa_Rica', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(ts))
        : '');
      setNumeroEditando(b.numero);
      setFecha(b.fecha);
      setObservacion(b.observacion || '');
      setTrabajadores(((tr ?? []) as any[]).map((t) => {
        const lab = labores.find((l) => l.id === t.labor_id);
        const hi = hhmm(t.entrada_at);
        const hf = hhmm(t.salida_at);
        // Sin entrada/salida pero con horas: conservar las horas digitadas.
        const horasSinHorario = !hi && !hf ? Number(t.horas_ordinarias || 0) + Number(t.horas_extra || 0) : 0;
        const excepcion = t.estado_asistencia === 'vacaciones' || t.observacion === 'vacaciones' ? 'vacaciones' : (t.observacion || '');
        return {
          ...nuevaLinea(),
          nombre: t.trabajador_nombre,
          codigo: t.trabajador_codigo || '',
          laborId: excepcion === 'vacaciones' ? '' : t.labor_id || '',
          laborTexto: excepcion === 'vacaciones' ? '' : lab ? `${lab.codigo_vfp} - ${lab.nombre}` : '',
          // Boletas anteriores al lote por fila: se toma el lote del encabezado.
          loteId: excepcion === 'vacaciones' ? '' : t.lote_id || b.lote_id || '',
          horas: horasSinHorario > 0 ? String(horasSinHorario) : '',
          produccion: Number(t.cantidad_produccion || 0) ? String(t.cantidad_produccion) : '',
          horaInicio: hi,
          horaFin: hf,
          excepcion,
          jornada: excepcion === 'vacaciones' ? 8 : Number(t.jornada_ordinaria_horas || 0) || JORNADA_ORDINARIA_DEFAULT,
        };
      }));
      setCargandoBoleta(false);
    })();
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boletaId, empresaId]);

  async function eliminar() {
    if (!boletaId) return;
    setSaving(true);
    // Hijas primero (bloques, labores, trabajadores, gps) y luego la boleta.
    const rs = await Promise.all([
      supabase.from('campo_boleta_trabajadores').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
      supabase.from('campo_boleta_labores').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
      supabase.from('campo_boleta_bloques').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
      supabase.from('campo_boleta_gps').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
    ]);
    const e1 = rs.find((r) => r.error)?.error;
    const { error: e2 } = e1 ? { error: e1 } : await supabase.from('campo_boletas').delete().eq('id', boletaId).eq('empresa_id', empresaId);
    setSaving(false);
    if (e2) { onError(e2.message); return; }
    logModuloEvento({ empresaId, modulo: 'campo_labores', accion: 'eliminar', entidad: 'campo_boletas', entidadId: boletaId, descripcion: `Boleta de labores ${numeroEditando} eliminada` });
    onSaved();
  }

  // Como en Jornadas de Planta: la boleta se guarda ABIERTA (sin revisión ni
  // aprobación por boleta) y se puede corregir hasta el cierre semanal.
  async function guardar() {
    const estado: EstadoBoleta = 'borrador';
    if (!fecha) {
      onError('La fecha es requerida.');
      return;
    }
    if (lineasValidas.length === 0) {
      onError('Agregue al menos un colaborador en asistencia.');
      return;
    }
    // Quien no trabajó ese día (ausente, incapacidad, permiso, vacaciones) no necesita labor.
    const sinLabor = lineasValidas.find((t) => !t.laborId && !MOTIVOS_SIN_HORAS.includes(t.excepcion));
    if (sinLabor) {
      onError(`Falta la labor del colaborador ${sinLabor.nombre.trim() || '(sin nombre)'}.`);
      return;
    }
    const sinLote = lineasValidas.find((t) => !t.loteId && !MOTIVOS_SIN_HORAS.includes(t.excepcion));
    if (sinLote) {
      onError(`Falta el lote del colaborador ${sinLote.nombre.trim() || '(sin nombre)'}.`);
      return;
    }
    // Labores presentes en la boleta (vía colaboradores).
    const laboresPresentes = Array.from(new Set(lineasValidas.map((t) => t.laborId)))
      .map((id) => laborDe(id))
      .filter((l): l is Labor => !!l);
    // Encabezado: resumen de los lotes de la boleta (uno solo o la lista).
    const lotesBoleta = Array.from(new Set(lineasValidas.map((t) => t.loteId).filter(Boolean)))
      .map((id) => loteDe(id)).filter((l): l is Lote => !!l)
      .sort((a, b) => a.numero.localeCompare(b.numero, 'es', { numeric: true }));
    const fincasBoleta = Array.from(new Set(lotesBoleta.map((l) => l.finca_id)));
    const estadiosBoleta = Array.from(new Set(lotesBoleta.map((l) => estadioDeLote(l.id)).filter(Boolean)));
    const fincaUnica = fincasBoleta.length === 1 ? fincas.find((f) => f.id === fincasBoleta[0]) : undefined;
    setSaving(true);
    let nextNum: string = numeroEditando;
    if (!boletaId) {
      const { data: n, error: eNum } = await supabase.rpc('campo_next_boleta', { p_empresa_id: empresaId });
      if (eNum) {
        setSaving(false);
        onError(eNum.message);
        return;
      }
      nextNum = String(n || '');
    }
    const { data: userData } = await supabase.auth.getUser();

    const boleta = {
      empresa_id: empresaId,
      numero: String(nextNum || ''),
      fecha,
      creado_por: userData.user?.id ?? null,
      semana: weekLabel(fecha),
      supervisor_nombre: supervisorNombre || null,
      finca_id: fincaUnica?.id ?? null,
      finca_nombre: fincaUnica?.nombre ?? (fincasBoleta.length > 1 ? fincasBoleta.map((id) => fincas.find((f) => f.id === id)?.nombre).filter(Boolean).join(', ') : null),
      lote_id: lotesBoleta.length === 1 ? lotesBoleta[0].id : null,
      lote_num: lotesBoleta.map((l) => l.numero).join(', ') || null,
      estadio_codigo: estadiosBoleta.length === 1 ? estadiosBoleta[0] : null,
      estado,
      costo_total: costo,
      observacion: observacion.trim() || null,
    };

    let idBoleta = boletaId || '';
    if (boletaId) {
      // Editar: encabezado + reemplazo de bloques, labores y colaboradores.
      const { creado_por: _c, numero: _n, ...cambios } = boleta;
      const { error: eUpd } = await supabase.from('campo_boletas').update(cambios).eq('id', boletaId).eq('empresa_id', empresaId);
      const rs = eUpd ? [] : await Promise.all([
        supabase.from('campo_boleta_trabajadores').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
        supabase.from('campo_boleta_labores').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
        supabase.from('campo_boleta_bloques').delete().eq('boleta_id', boletaId).eq('empresa_id', empresaId),
      ]);
      const eDel = eUpd || rs.find((r) => r.error)?.error;
      if (eDel) {
        setSaving(false);
        onError(eDel.message || 'No se pudo guardar la boleta.');
        return;
      }
    } else {
      const { data: inserted, error: eIns } = await supabase.from('campo_boletas').insert(boleta).select('id').single();
      if (eIns || !inserted) {
        setSaving(false);
        onError(eIns?.message || 'No se pudo guardar la boleta.');
        return;
      }
      idBoleta = inserted.id;
    }

    // Una fila campo_boleta_labores por cada labor distinta presente en la boleta,
    // con sus horas y costo agregados desde los colaboradores de esa labor.
    const laborRows = laboresPresentes.map((lab) => {
      const deEsta = lineasValidas.filter((t) => t.laborId === lab.id);
      const horasLab = deEsta.reduce((s, t) => s + horasDesdeLinea(t), 0);
      const costoLab = deEsta.reduce((s, t) => {
        const tarifaLinea = tarifaHoraDe(t, lab);
        if (lab.modalidad_default === 'fijo') return s + tarifaLinea;
        if (lab.modalidad_default === 'produccion' || lab.modalidad_default === 'tarea') {
          return s + Number(t.produccion || 0) * tarifaLinea;
        }
        const totalLinea = horasDesdeLinea(t);
        return s + montoPorHoras(horasOrdinarias(totalLinea, jornadaDe(t)), horasExtra(totalLinea, jornadaDe(t)), tarifaLinea);
      }, 0);
      return {
        empresa_id: empresaId,
        boleta_id: idBoleta,
        labor_id: lab.id,
        labor_nombre: lab.nombre,
        modalidad: lab.modalidad_default,
        unidad: lab.unidad_default,
        cantidad: deEsta.reduce((s, t) => s + Number(t.produccion || 0), 0),
        horas: horasLab,
        tarifa: lab.tarifa_base ?? 0,
        costo_labor: costoLab,
        distribucion_costo: 'area',
        observacion: null,
      };
    });
    const trabajadorRows = lineasValidas.map((t) => {
      const lab = laborDe(t.laborId);
      const total = horasDesdeLinea(t);
      const tarifaLinea = tarifaHoraDe(t, lab);
      const loteFila = t.loteId ? loteDe(t.loteId) : undefined;
      const monto = lab?.modalidad_default === 'fijo'
        ? tarifaLinea
        : (lab?.modalidad_default === 'produccion' || lab?.modalidad_default === 'tarea')
          ? Number(t.produccion || 0) * tarifaLinea
          : montoPorHoras(horasOrdinarias(total, jornadaDe(t)), horasExtra(total, jornadaDe(t)), tarifaLinea);
      return {
        empresa_id: empresaId,
        boleta_id: idBoleta,
        labor_id: t.laborId || null,
        labor_nombre: lab?.nombre || null,
        lote_id: loteFila?.id ?? null,
        lote_num: loteFila?.numero ?? null,
        finca_id: loteFila?.finca_id ?? null,
        estadio_codigo: loteFila ? (estadioDeLote(loteFila.id) || null) : null,
        trabajador_codigo: t.codigo.trim() || null,
        trabajador_nombre: t.nombre.trim(),
        estado_asistencia: asistenciaDesdeExcepcion(t.excepcion),
        entrada_at: buildDateTime(fecha, t.horaInicio),
        salida_at: buildDateTime(fecha, t.horaFin),
        horas_ordinarias: horasOrdinarias(total, jornadaDe(t)),
        horas_extra: horasExtra(total, jornadaDe(t)),
        jornada_ordinaria_horas: jornadaDe(t),
        cantidad_produccion: Number(t.produccion || 0),
        tarifa_aplicada: tarifaLinea,
        monto_calculado: monto,
        observacion: t.excepcion || null,
      };
    });
    const [rLabor, rTrab] = await Promise.all([
      supabase.from('campo_boleta_labores').insert(laborRows),
      trabajadorRows.length ? supabase.from('campo_boleta_trabajadores').insert(trabajadorRows) : Promise.resolve({ error: null }),
    ]);

    setSaving(false);
    const err = rLabor.error || rTrab.error;
    if (err) {
      onError(err.message);
      return;
    }
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: boletaId ? 'editar' : 'crear',
      entidad: 'campo_boletas',
      entidadId: idBoleta,
      descripcion: `Boleta de labores ${boleta.numero} ${boletaId ? 'corregida' : 'creada'}`,
    });
    onSaved();
  }

  return (
    <div className="space-y-4" onKeyDown={onEnterAvanza}>
      {/* ── Contenedor superior: datos fijos + bloques (ancho completo) ── */}
      {cargandoBoleta && (
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink-faint)' }}><Loader2 size={14} className="animate-spin" /> Cargando boleta…</div>
      )}
      {numeroEditando && (
        <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Boleta {numeroEditando} · Abierta (se puede corregir hasta el cierre semanal)</div>
      )}
      <div className="rounded-lg border p-4" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
        <div className="grid grid-cols-1 gap-3">
          {/* Datos fijos */}
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Grupo contable</label>
              {grupoUnico ? (
                <div className="rounded border px-3 py-2 text-sm font-semibold" style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)', color: 'var(--ink)' }}>
                  {grupos[0].nombre} ({colaboradores.filter((c) => grupoDeColab(c) === grupos[0].id).length})
                </div>
              ) : (
                <select data-nav="grupo" className={selectCls} value={grupoValido ? grupoId : ''} onChange={(e) => cambiarGrupo(e.target.value)}>
                  <option value="">Seleccione el grupo antes de agregar colaboradores</option>
                  {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre} ({colaboradores.filter((c) => grupoDeColab(c) === g.id).length})</option>)}
                </select>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="min-w-0 overflow-hidden rounded">
                <label className={labelCls}>Fecha</label>
                <input type="date" data-nav="fecha" className={`${inputCls} min-w-0`} style={{ maxWidth: '100%', boxSizing: 'border-box' }} value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Supervisor autenticado</label>
                <div
                  className="rounded border px-3 py-2 text-sm font-semibold"
                  style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)', color: 'var(--ink)' }}
                >
                  {supervisorNombre || 'Usuario actual'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Observación + resumen + acciones (arriba, no interfieren con los dropdowns de la tabla) */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <label className={labelCls}>Observacion</label>
            <textarea className={`${inputCls} min-h-20`} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
          </div>
          <div className="flex flex-col justify-between gap-3">
            <div className="flex items-baseline gap-4 text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>
              <span className="font-bold">Resumen</span>
              <span><span className="font-mono text-base font-semibold" style={{ color: 'var(--ink-muted)' }}>{personas}</span> persona(s)</span>
              <span><span className="font-mono text-base font-semibold" style={{ color: 'var(--ink-muted)' }}>{fmtHM(totalHorasTrabajadores)}</span> horas</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {boletaId && (
                <button type="button" className={`${btnDanger} flex items-center justify-center gap-2`} disabled={saving || cargandoBoleta}
                  onClick={() => { if (confirmarEliminar) void eliminar(); else setConfirmarEliminar(true); }}>
                  <X size={14} /> {confirmarEliminar ? '¿Seguro? Eliminar' : 'Eliminar boleta'}
                </button>
              )}
              <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
              <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={() => void guardar()} disabled={saving || cargandoBoleta}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Contenedor inferior: asistencia (ancho completo) ── */}
      <div className="rounded-lg border p-4" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>Asistencia</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <button type="button" className={`${btnSecondary} flex items-center gap-2`} onClick={agregarTodoPersonal}
              title="Agrega a todo el personal activo del grupo (con la labor y el horario de la fila de captura, si los hay)">
              <Users size={14} /> Todo el personal
            </button>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: 'var(--ink-faint)' }}>
              <span className="uppercase tracking-wider">Jornada ord.</span>
              <input
                className={`${inputCls} px-2 text-right`}
                style={{ width: 56 }}
                type="number"
                step="0.5"
                min="0"
                data-nav="jornada"
                value={jornadaOrdinaria}
                onChange={(e) => setJornadaOrdinaria(e.target.value)}
                title="Horas de jornada ordinaria para los colaboradores que agregue a partir de ahora (lo que exceda es extra). No cambia a los ya incluidos."
              />
              <span style={{ color: 'var(--ink-faint)' }}>h</span>
            </label>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: 'var(--ink-faint)' }}>
              <span className="uppercase tracking-wider">Entrada</span>
              <input
                className={`${inputCls} px-1`}
                style={{ width: 96 }}
                type="time"
                data-nav="entrada"
                value={entradaDia}
                onChange={(e) => setEntradaDiaSync(e.target.value)}
                title="Entrada estándar del día (pre-llena cada empleado)"
              />
            </label>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: 'var(--ink-faint)' }}>
              <span className="uppercase tracking-wider">Salida</span>
              <input
                className={`${inputCls} px-1`}
                style={{ width: 96 }}
                type="time"
                data-nav="salida"
                value={salidaDia}
                onChange={(e) => setSalidaDiaSync(e.target.value)}
                title="Salida estándar del día"
              />
            </label>
          </div>
        </div>

        {/* Fila de captura: se llena aquí y se agrega a la tabla */}
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-[1.3fr_1.3fr_0.9fr_auto_auto_auto]" style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)' }}>
          <div>
            <label className={labelCls}>Empleado</label>
            <button
              type="button"
              className={`${inputCls} flex w-full items-center justify-between gap-2 text-left`}
              data-nav="empleado"
              style={{ color: captura.nombre ? 'var(--ink)' : 'var(--ink-faint)' }}
              onClick={() => (grupoValido ? setModalEmpleado(true) : onError('Seleccione primero el grupo contable de la boleta.'))}
            >
              <span className="truncate">
                {captura.codigo && <span className="mr-2 font-mono font-semibold" style={{ color: 'var(--accent)' }}>{captura.codigo}</span>}
                {captura.nombre || (grupoValido ? 'Seleccionar colaborador (F3)' : 'Primero elija el grupo contable')}
              </span>
              <Search size={14} style={{ color: 'var(--ink-faint)', flexShrink: 0 }} />
            </button>
          </div>
          <div>
            <label className={labelCls}>Labor</label>
            <div className="relative">
              <input
                className={`${inputCls} w-full pr-9`}
                data-nav="labor"
                value={laborDigitada}
                placeholder="Código de labor (Enter) o buscar"
                onChange={(e) => {
                  setLaborDigitada(e.target.value);
                  // Si cambia el texto de una labor ya elegida, se deselecciona hasta confirmar otra.
                  if (captura.laborId && e.target.value !== captura.laborTexto) {
                    setCaptura((prev) => ({ ...prev, laborId: '', laborTexto: '' }));
                  }
                }}
                onKeyDown={onLaborKeyDown}
                onBlur={onLaborBlur}
                onFocus={(e) => e.target.select()}
                title="Digite el código de la labor y Enter; si no existe, se abre la búsqueda"
              />
              <button
                type="button"
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1"
                style={{ color: 'var(--ink-faint)' }}
                onClick={() => setModalLabor(true)}
                title="Buscar labor"
                aria-label="Buscar labor"
              >
                <Search size={14} />
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Lote</label>
            <select data-nav="lote" className={selectCls} value={captura.loteId}
              title="Lote donde trabajó (la labor aplica a todos sus bloques). Se conserva para el siguiente colaborador."
              onChange={(e) => setCaptura((prev) => ({ ...prev, loteId: e.target.value }))}>
              <option value="">Seleccione</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{etiquetaLote(l)}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>HI–HF</label>
            <div className="flex items-center gap-1">
              <input className={`${inputCls} px-1`} style={{ width: 96 }} type="time" data-nav="hi" value={captura.horaInicio} onChange={(e) => editarCaptura('horaInicio', e.target.value)} title="Hora inicio" />
              <input className={`${inputCls} px-1`} style={{ width: 96 }} type="time" data-nav="hf" value={captura.horaFin} onChange={(e) => editarCaptura('horaFin', e.target.value)} title="Hora fin" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Motivo</label>
            <select data-nav="motivo" className={selectCls} style={{ minWidth: 130 }} value={captura.excepcion} onChange={(e) => editarCaptura('excepcion', e.target.value)}>
              {OPCIONES_MOTIVO.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-1">
            <button type="button" data-nav="agregar" className={`${btnPrimary} flex items-center justify-center`} onClick={agregarCaptura} title="Agregar colaborador" aria-label="Agregar colaborador">
              <Plus size={18} />
            </button>
            {cuadrillas.length > 0 && (
              <button type="button" className={`${btnSecondary} flex items-center justify-center`} onClick={() => setModalCuadrilla(true)} title="Agregar cuadrilla completa" aria-label="Agregar cuadrilla completa">
                <Users size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Lista de colaboradores agregados — tarjetas tipo acordeón (mejor en pantallas angostas que una tabla ancha) */}
        <div className="space-y-2">
          {trabajadores.length === 0 ? (
            <div className="rounded-lg border px-3 py-6 text-center text-sm" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
              Sin colaboradores. Use la fila de arriba para agregarlos.
            </div>
          ) : trabajadores.map((t) => {
            const total = horasDesdeLinea(t);
            const norm = horasOrdinarias(total, jornadaDe(t));
            const extra = horasExtra(total, jornadaDe(t));
            const lab = laborDe(t.laborId);
            const sinHoras = MOTIVOS_SIN_HORAS.includes(t.excepcion);
            const cls = 'rounded border border-line bg-surface-raised px-2 py-1 text-xs text-ink focus:outline-none focus-ring';
            return (
              <div key={t.key} className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                <div className="min-w-0 flex-1" style={{ flexBasis: 220 }}>
                  <div className="truncate text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                    <span className="mr-2 font-mono font-semibold" style={{ color: 'var(--accent)' }}>{t.codigo}</span>
                    {t.nombre}
                  </div>
                </div>
                {/* Labor: código + Enter, o la lupa para buscar */}
                <div className="flex shrink-0 items-center gap-1" style={{ width: 300 }}>
                  <input
                    key={`${t.key}-${t.laborId}`}
                    className={`${cls} font-mono`}
                    style={{ width: 70 }}
                    defaultValue={lab?.codigo_vfp || ''}
                    placeholder="Labor"
                    title="Código de la labor (Enter)"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); laborEnFila(t.key, e.currentTarget.value, true); } }}
                    onBlur={(e) => { if (e.currentTarget.value.trim() !== (lab?.codigo_vfp || '')) laborEnFila(t.key, e.currentTarget.value, false); }}
                  />
                  <button type="button" className="rounded p-1" style={{ color: 'var(--ink-faint)' }} title="Buscar labor"
                    onClick={() => { setLaborFila({ key: t.key, texto: '' }); setModalLabor(true); }}>
                    <Search size={13} />
                  </button>
                  <span className="min-w-0 flex-1 truncate text-xs" style={{ color: lab ? 'var(--ink-muted)' : 'var(--tone-warn-text)' }} title={lab?.nombre}>
                    {lab ? lab.nombre : (sinHoras ? '—' : 'Sin labor')}
                  </span>
                </div>
                <select
                  className={cls}
                  style={{ width: 150, flex: '0 0 150px', color: !t.loteId && !sinHoras ? 'var(--tone-warn-text)' : undefined }}
                  value={t.loteId}
                  disabled={t.excepcion === 'vacaciones'}
                  title="Lote donde trabajó"
                  onChange={(e) => setFila(t.key, { loteId: e.target.value })}
                >
                  <option value="">{sinHoras ? '—' : 'Sin lote'}</option>
                  {lotes.map((l) => <option key={l.id} value={l.id}>{etiquetaLote(l)}</option>)}
                </select>
                <select
                  className={cls}
                  style={{ width: 140, flex: '0 0 140px', color: t.excepcion ? 'var(--tone-warn-text)' : undefined }}
                  value={t.excepcion}
                  title="Asistencia"
                  onChange={(e) => cambiarAsistencia(t.key, e.target.value)}
                >
                  {OPCIONES_MOTIVO.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <input type="time" className={cls} style={{ width: 96 }} value={t.horaInicio} disabled={sinHoras} title="Hora de inicio"
                  onChange={(e) => horaEnFila(t, 'horaInicio', e.target.value)} />
                <input type="time" className={cls} style={{ width: 96 }} value={t.horaFin} disabled={sinHoras} title="Hora final"
                  onChange={(e) => horaEnFila(t, 'horaFin', e.target.value)} />
                {/* Jornada ordinaria de ESTA fila (arranca con la de arriba; 8 h a uno, 9 h a otro) */}
                <div className="flex shrink-0 items-center gap-1" title="Jornada ordinaria de este colaborador (h)">
                  <input type="number" min={0} max={24} step={0.5} className={`${cls} text-right font-mono`} style={{ width: 58 }}
                    value={t.jornada || jornadaNum} disabled={sinHoras}
                    onChange={(e) => setFila(t.key, { jornada: Math.max(0, Number(e.target.value) || 0) })} />
                  <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>h jor.</span>
                </div>
                <div className="shrink-0 text-right font-mono text-sm font-semibold" style={{ width: 84, color: 'var(--ink)' }} title="Horas ordinarias + extra">
                  {fmtHM(norm)}
                  {extra > 0 && <span style={{ color: 'var(--tone-warn-text)' }}> +{fmtHM(extra)}</span>}
                </div>
                <button type="button" className="shrink-0 rounded p-1.5" style={{ color: '#f87171' }} onClick={() => quitarTrabajador(t.key)} title="Quitar colaborador">
                  <X size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modales de selección (createPortal a document.body) */}
      {modalEmpleado && (
        <SelectorModal
          titulo="Seleccionar colaborador"
          placeholder="Buscar por código o nombre…"
          opciones={colaboradoresGrupo.map((c) => ({
            id: String(c.id),
            codigo: c.numero_empleado || c.identificacion || String(c.id),
            titulo: c.nombre_completo,
          }))}
          onSelect={(o) => {
            const c = colaboradoresGrupo.find((x) => String(x.id) === o.id);
            if (c) elegirEmpleadoCaptura(c);
          }}
          onClose={() => setModalEmpleado(false)}
          accion={{ label: 'Registrar colaborador nuevo', onClick: (texto) => { setModalEmpleado(false); setAltaTexto(texto); } }}
        />
      )}
      {altaTexto !== null && (
        <AltaRapidaColaborador
          empresaId={empresaId}
          grupoNombre={grupoNombre}
          departamentos={departamentosGrupo}
          registradoPor={supervisorNombre || 'usuario de Aplicaciones'}
          textoInicial={altaTexto}
          onClose={() => setAltaTexto(null)}
          onCreado={(c) => { onColaboradorCreado(c); setAltaTexto(null); elegirEmpleadoCaptura(c); }}
        />
      )}
      {modalLabor && (
        <SelectorModal
          titulo="Seleccionar labor"
          placeholder="Buscar por código o nombre…"
          inicial={laborFila ? laborFila.texto : (captura.laborId ? '' : laborDigitada.trim())}
          opciones={labores.map((l) => ({
            id: l.id,
            codigo: l.codigo_vfp,
            titulo: l.nombre,
            subtitulo: l.modalidad_default,
          }))}
          onSelect={(o) => {
            const l = labores.find((x) => x.id === o.id);
            if (!l) return;
            if (laborFila) {
              setFila(laborFila.key, { laborId: l.id, laborTexto: `${l.codigo_vfp} - ${l.nombre}` });
              setLaborFila(null);
              setModalLabor(false);
            } else {
              elegirLaborCaptura(l);
            }
          }}
          onClose={() => { setModalLabor(false); setLaborFila(null); }}
        />
      )}
      {modalCuadrilla && (
        <SelectorModal
          titulo="Agregar cuadrilla completa"
          placeholder="Buscar por codigo o nombre…"
          opciones={cuadrillas.map((c) => ({
            id: c.id,
            codigo: c.codigo,
            titulo: c.nombre,
            subtitulo: c.supervisor_nombre || undefined,
          }))}
          onSelect={(o) => {
            const c = cuadrillas.find((x) => x.id === o.id);
            if (c) agregarCuadrilla(c);
          }}
          onClose={() => setModalCuadrilla(false)}
        />
      )}
    </div>
  );
}

interface BoletaBloqueRow { bloque_num: string | null; area_ha: number | null; plantas: number | null }
interface BoletaLaborRow { labor_nombre: string; modalidad: string; unidad: string; cantidad: number; horas: number; costo_labor: number }
interface BoletaTrabajadorRow {
  trabajador_codigo: string | null;
  trabajador_nombre: string;
  labor_nombre: string | null;
  lote_num: string | null;
  horas_ordinarias: number | null;
  horas_extra: number | null;
  monto_calculado: number | null;
  estado_jornada: string | null;
  jornada_ordinaria_horas: number | null;
  estado_asistencia: string | null;
  observacion: string | null;
}

// Detalle de una boleta ya guardada: datos completos + flujo de estado
// (revisar/aprobar/rechazar/anular) + impresion. Las jornadas individuales
// se corrigen en la pantalla de Jornadas; aqui solo se ven y se decide sobre
// la boleta completa.
function BoletaDetalle({
  empresaId,
  boletaId,
  permisos,
  onClose,
  onChanged,
  onError,
}: {
  empresaId: number;
  boletaId: string;
  permisos: AppPermisos;
  onClose: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [boleta, setBoleta] = useState<CampoBoleta | null>(null);
  const [bloques, setBloques] = useState<BoletaBloqueRow[]>([]);
  const [labores, setLabores] = useState<BoletaLaborRow[]>([]);
  const [trabajadores, setTrabajadores] = useState<BoletaTrabajadorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [accionando, setAccionando] = useState(false);
  const [pidiendoMotivo, setPidiendoMotivo] = useState<'rechazar' | 'anular' | null>(null);
  const [motivo, setMotivo] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    const [{ data: b }, { data: bl }, { data: lb }, { data: tr }] = await Promise.all([
      supabase.from('campo_boletas').select('*').eq('id', boletaId).eq('empresa_id', empresaId).single(),
      supabase.from('campo_boleta_bloques').select('bloque_num, area_ha, plantas').eq('boleta_id', boletaId).eq('empresa_id', empresaId),
      supabase.from('campo_boleta_labores').select('labor_nombre, modalidad, unidad, cantidad, horas, costo_labor').eq('boleta_id', boletaId).eq('empresa_id', empresaId),
      supabase.from('campo_boleta_trabajadores').select('trabajador_codigo, trabajador_nombre, labor_nombre, lote_num, horas_ordinarias, horas_extra, monto_calculado, estado_jornada, jornada_ordinaria_horas, estado_asistencia, observacion').eq('boleta_id', boletaId).eq('empresa_id', empresaId).order('trabajador_nombre'),
    ]);
    setBoleta((b ?? null) as CampoBoleta | null);
    setBloques((bl ?? []) as BoletaBloqueRow[]);
    setLabores((lb ?? []) as BoletaLaborRow[]);
    setTrabajadores((tr ?? []) as BoletaTrabajadorRow[]);
    setLoading(false);
  }, [empresaId, boletaId]);

  useEffect(() => { cargar(); }, [cargar]);

  async function accion(rpc: string, extra?: Record<string, unknown>) {
    setAccionando(true);
    const { data, error } = await supabase.rpc(rpc, { p_empresa_id: empresaId, p_boleta_id: boletaId, ...extra });
    setAccionando(false);
    if (error) { onError(error.message); return; }
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: rpc.replace('campo_boleta_', ''),
      entidad: 'campo_boletas',
      entidadId: boletaId,
      descripcion: `Boleta ${boleta?.numero || boletaId} -> ${data?.estado || rpc}`,
    });
    setPidiendoMotivo(null);
    setMotivo('');
    onChanged();
    cargar();
  }

  const jornadaComun = Number(trabajadores[0]?.jornada_ordinaria_horas ?? JORNADA_ORDINARIA_DEFAULT);
  const hayJornadasSinResolver = trabajadores.some((t) => t.estado_jornada === 'pendiente' || t.estado_jornada === 'corregida' || !t.estado_jornada);

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-start justify-center overflow-auto p-4 sm:p-8" style={{ background: 'rgba(0,0,0,0.55)' }} onMouseDown={onClose}>
      <div className="mt-6 w-full max-w-3xl rounded-xl border shadow-2xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b px-5 py-3" style={{ borderColor: 'var(--line)' }}>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{boleta?.numero || '...'}</span>
            {boleta && <EstadoBadge estado={boleta.estado} />}
          </div>
          <div className="flex items-center gap-2">
            {boleta && (
              <button type="button" className="rounded p-1.5" style={{ color: 'var(--ink-faint)' }} onClick={() => imprimirBoletaLabores({ boleta, bloques, labores, trabajadores })} title="Imprimir">
                <Printer size={16} />
              </button>
            )}
            <button type="button" className="rounded p-1" style={{ color: 'var(--ink-faint)' }} onClick={onClose} title="Cerrar">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-auto p-5">
          {loading || !boleta ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm" style={{ color: 'var(--ink-faint)' }}>
              <Loader2 size={18} className="animate-spin" /> Cargando boleta...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div><div className={labelCls}>Fecha</div><div style={{ color: 'var(--ink)' }}>{fmtDate(boleta.fecha)}</div></div>
                <div><div className={labelCls}>Ubicacion</div><div style={{ color: 'var(--ink)' }}>{boleta.finca_nombre || '-'} / {String(boleta.lote_num || '').includes(',') ? 'Lotes' : 'Lote'} {boleta.lote_num || '-'}</div></div>
                <div><div className={labelCls}>Supervisor</div><div style={{ color: 'var(--ink)' }}>{boleta.supervisor_nombre || '-'}</div></div>
                <div><div className={labelCls}>Costo</div><div className="font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(boleta.costo_total)}</div></div>
              </div>

              {boleta.observacion && (
                <div className="rounded-lg border px-3 py-2 text-xs" style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                  {boleta.observacion}
                </div>
              )}

              <div>
                <div className={labelCls}>Labores</div>
                <div className={tableWrapCls}>
                  <table className="w-full border-collapse text-xs">
                    <thead className={theadCls}><tr><th className={thCls}>Labor</th><th className={`${thCls} text-right`}>Horas</th><th className={`${thCls} text-right`}>Unid.</th><th className={`${thCls} text-right`}>Costo</th></tr></thead>
                    <tbody>
                      {labores.map((l, i) => (
                        <tr key={i} className={trCls}>
                          <td className={tdCls}>{l.labor_nombre}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtHM(Number(l.horas || 0))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{Number(l.cantidad || 0) || '-'}</td>
                          <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(l.costo_labor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {bloques.length > 0 && <div>
                <div className={labelCls}>Bloques</div>
                <div className="flex flex-wrap gap-2">
                  {bloques.length ? bloques.map((b, i) => (
                    <span key={i} className="rounded border px-2 py-1 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>Bloque {b.bloque_num}</span>
                  )) : null}
                </div>
              </div>}

              <div>
                <div className={labelCls}>Trabajadores</div>
                <div className={tableWrapCls}>
                  <table className="w-full border-collapse text-xs">
                    <thead className={theadCls}><tr><th className={thCls}>Empleado</th><th className={thCls}>Labor</th><th className={thCls}>Lote</th><th className={thCls}>Asistencia</th><th className={`${thCls} text-right`}>H. Norm.</th><th className={`${thCls} text-right`}>H. Extra</th><th className={`${thCls} text-right`}>Monto</th><th className={thCls}>Jornada</th></tr></thead>
                    <tbody>
                      {trabajadores.map((t, i) => (
                        <tr key={i} className={trCls}>
                          <td className={tdCls}><span className="mr-1 font-mono font-semibold" style={{ color: 'var(--accent)' }}>{t.trabajador_codigo}</span>{t.trabajador_nombre}</td>
                          <td className={tdCls}>{t.labor_nombre || '-'}</td>
                          <td className={tdCls}>{t.lote_num || boleta.lote_num || '-'}</td>
                          <td className={tdCls} style={{ color: t.estado_asistencia === 'vacaciones' || t.observacion ? 'var(--tone-warn-text)' : undefined }}>{t.estado_asistencia === 'vacaciones' ? 'Vacaciones' : t.observacion ? (MOTIVO_LABEL[t.observacion] || t.observacion) : 'Normal'}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtHM(t.estado_asistencia === 'vacaciones' ? 8 : Number(t.horas_ordinarias || 0))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtHM(t.estado_asistencia === 'vacaciones' ? 0 : Number(t.horas_extra || 0))}</td>
                          <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(t.monto_calculado)}</td>
                          <td className={tdCls} style={{ color: t.estado_jornada === 'aprobada' ? 'var(--tone-success-text)' : t.estado_jornada === 'rechazada' ? '#f87171' : 'var(--tone-info-text)' }}>{t.estado_jornada || 'pendiente'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {hayJornadasSinResolver && boleta.estado !== 'aprobada' && (
                  <div className="mt-2 text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
                    Hay jornadas sin resolver — al aprobar la boleta tambien se aprueban de una vez.
                  </div>
                )}
              </div>

              {pidiendoMotivo && (
                <div className="rounded-lg border p-3" style={{ background: 'var(--surface-overlay)', borderColor: 'var(--line)' }}>
                  <label className={labelCls}>Motivo de {pidiendoMotivo === 'rechazar' ? 'rechazo' : 'anulacion'}</label>
                  <textarea className={`${inputCls} min-h-16`} value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
                  <div className="mt-2 flex justify-end gap-2">
                    <button type="button" className={btnSecondary} onClick={() => { setPidiendoMotivo(null); setMotivo(''); }}>Cancelar</button>
                    <button
                      type="button"
                      className={btnDanger}
                      disabled={accionando || !motivo.trim()}
                      onClick={() => accion(pidiendoMotivo === 'rechazar' ? 'campo_boleta_rechazar' : 'campo_boleta_anular', { p_motivo: motivo })}
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {boleta && !pidiendoMotivo && (
          <div className="flex flex-wrap justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--line)' }}>
            {permisos.perm_vob && boleta.estado === 'pendiente' && (
              <button type="button" className={`${btnSecondary} flex items-center gap-2`} disabled={accionando} onClick={() => accion('campo_boleta_revisar')}>
                <Eye size={14} /> Revisar
              </button>
            )}
            {permisos.perm_vob && (boleta.estado === 'pendiente' || boleta.estado === 'revisada') && (
              <button type="button" className={`${btnPrimary} flex items-center gap-2`} disabled={accionando} onClick={() => accion('campo_boleta_aprobar_completa', { p_jornada_ordinaria_horas: jornadaComun })}>
                {accionando ? <Loader2 size={14} className="animate-spin" /> : <ThumbsUp size={14} />} Aprobar
              </button>
            )}
            {permisos.perm_vob && (boleta.estado === 'pendiente' || boleta.estado === 'revisada') && (
              <button type="button" className={`${btnSecondary} flex items-center gap-2`} disabled={accionando} onClick={() => setPidiendoMotivo('rechazar')}>
                <XCircle size={14} /> Rechazar
              </button>
            )}
            {permisos.perm_anular && boleta.estado !== 'anulada' && !boleta.cierre_semanal_id && (
              <button type="button" className={`${btnDanger} flex items-center gap-2`} disabled={accionando} onClick={() => setPidiendoMotivo('anular')}>
                <Ban size={14} /> Anular
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function BoletasLaboresPage({ supervisorNombre = '' }: { supervisorNombre?: string }) {
  const empresaId = useEmpresaId();
  const [boletas, setBoletas] = useState<CampoBoleta[]>([]);
  const [fincas, setFincas] = useState<Finca[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [bloques, setBloques] = useState<Bloque[]>([]);
  const [labores, setLabores] = useState<Labor[]>([]);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [grupos, setGrupos] = useState<GrupoContable[]>([]);
  const [deptoGrupo, setDeptoGrupo] = useState<Record<number, number | null>>({});
  const [departamentos, setDepartamentos] = useState<DepartamentoPl[]>([]);
  const [cuadrillas, setCuadrillas] = useState<Cuadrilla[]>([]);
  const [integrantesCuadrilla, setIntegrantesCuadrilla] = useState<CuadrillaIntegrante[]>([]);
  const [estado, setEstado] = useState<EstadoBoleta | ''>('');
  // La lista trabaja por semana (sábado a viernes), igual que el cierre semanal.
  const [fechaDesde, setFechaDesde] = useState(() => sabadoDeSemana(today()));
  const fechaHasta = addDays(fechaDesde, 6);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [capturando, setCapturando] = useState(false);
  // Boleta abierta que se está corrigiendo ('' = nueva).
  const [editandoId, setEditandoId] = useState('');
  const [resumenBoletas, setResumenBoletas] = useState<Record<string, { personas: number; horas: number }>>({});
  // Pestañas como en Jornadas de Planta: las boletas del día y el cierre de la semana.
  const [vista, setVista] = useState<'boletas' | 'cierre'>('boletas');
  const { esSuperUsuario } = useEsSuperUsuario();
  const [verBoletaId, setVerBoletaId] = useState('');
  const permisos = useAppPermisos();
  // Mismo criterio que la planilla del ERP: el grupo es el del departamento del
  // colaborador; sin departamento o departamento sin grupo → Comercial.
  const grupoDeColab = useCallback((c: Colaborador) => {
    const comercial = grupos.find((g) => g.codigo === 'comercial')?.id ?? null;
    return (c.departamento_id ? deptoGrupo[c.departamento_id] : null) ?? comercial;
  }, [grupos, deptoGrupo]);
  const sort = useTableSort<'numero' | 'fecha' | 'finca' | 'supervisor' | 'estado' | 'costo'>({ key: 'fecha', direction: 'desc' });

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    const [
      { data: b, error: eB },
      { data: f },
      { data: l },
      { data: bl },
      { data: lb },
      { data: cols },
      { data: cua },
      { data: integ },
      { data: grps },
      { data: dptos },
    ] = await Promise.all([
      supabase
        .from('campo_boletas')
        .select('id, numero, fecha, supervisor_nombre, finca_nombre, lote_num, estadio_codigo, estado, costo_total, inicio_lat, inicio_lng, fin_lat, fin_lng, cierre_semanal_id, created_at')
        .eq('empresa_id', empresaId)
        .gte('fecha', fechaDesde)
        .lte('fecha', fechaHasta)
        .order('fecha', { ascending: false })
        .limit(500),
      supabase.from('emp_fincas').select('id, nombre, codigo').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
      supabase.from('emp_lotes').select('id, finca_id, numero, nombre, area_ha').eq('empresa_id', empresaId).eq('activo', true).order('numero'),
      supabase.from('emp_bloques').select('id, lote_id, num, area_ha, plant_count, estadio_vfp').eq('empresa_id', empresaId).eq('activo', true).order('num'),
      supabase
        .from('campo_labores')
        .select('id, codigo_vfp, nombre, modalidad_default, unidad_default, tarifa_base, requiere_bloque, requiere_estadio, requiere_gps, requiere_produccion')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('codigo_vfp'),
      supabase
        .from('pl_colaboradores')
        .select('id, nombre_completo, numero_empleado, identificacion, estado, departamento_id')
        .eq('empresa_id', empresaId)
        .in('estado', ['activo', 'vacaciones', 'incapacitado'])
        .eq('modalidad_planilla', 'finca')   // los de planilla directa o de planta no van en boletas
        .order('nombre_completo'),
      supabase.from('campo_cuadrillas').select('id, codigo, nombre, supervisor_nombre').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
      supabase
        .from('campo_cuadrilla_integrantes')
        .select('cuadrilla_id, trabajador_codigo, trabajador_nombre, vigente_desde, vigente_hasta')
        .eq('empresa_id', empresaId)
        .eq('activo', true),
      supabase.from('pl_grupos_contables').select('id, codigo, nombre').eq('empresa_id', empresaId).eq('activo', true).order('orden'),
      supabase.from('pl_departamentos').select('id, nombre, grupo_contable_id, activo').eq('empresa_id', empresaId).order('nombre'),
    ]);
    setGrupos((grps ?? []) as GrupoContable[]);
    setDepartamentos((dptos ?? []) as DepartamentoPl[]);
    setDeptoGrupo(Object.fromEntries(((dptos ?? []) as DepartamentoPl[]).map((d) => [d.id, d.grupo_contable_id])));
    if (eB) setError(eB.message);
    setBoletas((b ?? []) as CampoBoleta[]);
    // Personas y horas por boleta (como la lista de Jornadas de Planta).
    const idsB = ((b ?? []) as CampoBoleta[]).map((x) => x.id);
    const res: Record<string, { personas: number; horas: number }> = {};
    if (idsB.length) {
      const { data: tr } = await supabase.from('campo_boleta_trabajadores').select('boleta_id, horas_ordinarias, horas_extra, estado_asistencia').in('boleta_id', idsB);
      for (const r of (tr ?? []) as { boleta_id: string; horas_ordinarias: number; horas_extra: number; estado_asistencia: string | null }[]) {
        const h = r.estado_asistencia === 'vacaciones' ? 8 : Number(r.horas_ordinarias || 0) + Number(r.horas_extra || 0);
        const x = (res[r.boleta_id] ??= { personas: 0, horas: 0 });
        if (h > 0) x.personas++;
        x.horas += h;
      }
    }
    setResumenBoletas(res);
    setFincas((f ?? []) as Finca[]);
    setLotes((l ?? []) as Lote[]);
    setBloques((bl ?? []) as Bloque[]);
    setLabores((lb ?? []) as Labor[]);
    setColaboradores((cols ?? []) as Colaborador[]);
    setCuadrillas((cua ?? []) as Cuadrilla[]);
    setIntegrantesCuadrilla((integ ?? []) as CuadrillaIntegrante[]);
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = boletas.filter((b) => {
      if (estado && b.estado !== estado) return false;
      if (!term) return true;
      return [
        b.numero,
        b.supervisor_nombre,
        b.finca_nombre,
        b.lote_num,
        b.estadio_codigo,
      ].some((x) => String(x ?? '').toLowerCase().includes(term));
    });
    return sortRows(base, sort.value, (row, key) => {
      if (key === 'finca') return row.finca_nombre;
      if (key === 'supervisor') return row.supervisor_nombre;
      if (key === 'costo') return Number(row.costo_total ?? 0);
      return row[key];
    });
  }, [boletas, estado, q, sort.value]);

  const stats = useMemo(() => {
    const pendientes = boletas.filter((b) => b.estado === 'pendiente').length;
    const aprobadas = boletas.filter((b) => b.estado === 'aprobada').length;
    const costo = boletas.reduce((s, b) => s + Number(b.costo_total ?? 0), 0);
    return { pendientes, aprobadas, costo };
  }, [boletas]);

  async function onSaved() {
    setCapturando(false);
    setEditandoId('');
    await cargar();
  }

  // "Volver" del navbar: desde una boleta (nueva o editando) o desde el cierre
  // semanal regresa a la lista de boletas, no a Inicio.
  const volverALista = useCallback(() => { setCapturando(false); setEditandoId(''); setVista('boletas'); }, []);
  const numeroEditando = editandoId ? boletas.find((b) => b.id === editandoId)?.numero : '';
  useNavbarSubtitulo(
    capturando ? (editandoId ? `Boleta ${numeroEditando || ''}`.trim() : 'Nueva boleta') : 'Cierre semanal',
    capturando || vista === 'cierre',
    volverALista,
  );

  async function onBoletaCambiada() {
    await cargar();
  }

  if (capturando) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-auto p-4 sm:p-5">
          {error && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
              <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
          )}
          <BoletaForm
            empresaId={empresaId}
            supervisorNombre={supervisorNombre}
            fincas={fincas}
            lotes={lotes}
            bloques={bloques}
            labores={labores}
            colaboradores={colaboradores}
            grupos={grupos.filter((g) => GRUPOS_APLICACIONES.includes(g.codigo))}
            grupoDeColab={grupoDeColab}
            departamentos={departamentos}
            onColaboradorCreado={(c) => setColaboradores((prev) => [...prev, c].sort((a, b) => a.nombre_completo.localeCompare(b.nombre_completo, 'es')))}
            cuadrillas={cuadrillas}
            integrantesCuadrilla={integrantesCuadrilla}
            onClose={() => { setCapturando(false); setEditandoId(''); }}
            onSaved={onSaved}
            onError={setError}
            boletaId={editandoId || undefined}
            key={editandoId || 'nueva'}
          />
        </div>
      </div>
    );
  }

  const puedeVerCierre = esSuperUsuario || permisos.perm_cierre_planilla || permisos.perm_vob || permisos.perm_boletas_labores;
  const puedeCapturarBoleta = esSuperUsuario || permisos.perm_generar_boletas || permisos.perm_boletas_labores;
  const pestanas = puedeVerCierre && (
    <div className="flex shrink-0 gap-1 px-5 pt-2" style={{ background: 'var(--surface-raised)' }}>
      {([['boletas', 'Boletas'], ['cierre', 'Cierre semanal']] as const).map(([v, label]) => (
        <button key={v} type="button" onClick={() => setVista(v)}
          className="border-b-2 px-4 py-2 text-xs font-semibold transition-colors"
          style={{ borderColor: vista === v ? 'var(--accent)' : 'transparent', color: vista === v ? 'var(--ink)' : 'var(--ink-faint)' }}>
          {label}
        </button>
      ))}
    </div>
  );

  if (vista === 'cierre' && puedeVerCierre) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        {pestanas}
        <div className="flex-1 overflow-auto">
          <CierreSemanalFincaPage usuarioNombre={supervisorNombre} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {pestanas}
      <div className="shrink-0 px-5 py-3" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-end">
          <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
            <div>
              <label className={labelCls}>Semana (cualquier día)</label>
              <input type="date" className={`${inputCls} w-full`} value={fechaDesde} onChange={(e) => e.target.value && setFechaDesde(sabadoDeSemana(e.target.value))} />
            </div>
            <div className="col-span-2 flex items-end gap-2 sm:col-span-1">
              <span className="whitespace-nowrap pb-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
                {diaSem(fechaDesde)} {fmtDate(fechaDesde)} al {diaSem(fechaHasta)} {fmtDate(fechaHasta)}
              </span>
              <button type="button" className={btnSecondary} onClick={() => setFechaDesde(addDays(fechaDesde, -7))}>← Anterior</button>
              <button type="button" className={btnSecondary} onClick={() => setFechaDesde(addDays(fechaDesde, 7))}>Siguiente →</button>
              {puedeVerCierre && (
                <button type="button" className={`${btnSecondary} flex items-center gap-1.5 whitespace-nowrap`}
                  title="Reporte de horas de la semana con las boletas aprobadas hasta hoy (no cierra la semana)"
                  onClick={async () => { setError(''); const e = await generarReporteHorasFinca(empresaId, fechaDesde, supervisorNombre); if (e) setError(e); }}>
                  <FileText size={14} /> Reporte de horas
                </button>
              )}
            </div>
            <div>
              <label className={labelCls}>Estado</label>
              <select className="w-full rounded border border-line bg-surface-overlay px-3 py-2 text-sm text-ink sm:w-44" value={estado} onChange={(e) => setEstado(e.target.value as EstadoBoleta | '')}>
                <option value="">Todos</option>
                {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input className={`${inputCls} w-full pl-8 sm:w-72`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Boleta, supervisor, lote" />
              </div>
            </div>
            <button type="button" className={`${btnSecondary} col-span-2 flex items-center justify-center gap-2 sm:col-span-1`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            {(permisos.perm_generar_boletas || permisos.perm_boletas_labores) && (
              <button type="button" className={`${btnPrimary} col-span-2 flex items-center justify-center gap-2 sm:col-span-1`} onClick={() => { setEditandoId(''); setCapturando(true); }} disabled={labores.length === 0}>
                <Plus size={14} />
                Nueva boleta
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi label="Boletas" value={boletas.length} icon={<ClipboardList size={14} />} tone="var(--emp-accent-txt)" />
          <Kpi label="Pendientes" value={stats.pendientes} icon={<Clock3 size={14} />} tone="var(--tone-info-text)" />
          <Kpi label="Aprobadas" value={stats.aprobadas} icon={<CheckCircle2 size={14} />} tone="var(--tone-success-text)" />
          <Kpi label="Costo" value={fmtCRC(stats.costo)} icon={<DollarSign size={14} />} tone="var(--tone-success-text)" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando boletas...
          </div>
        ) : filtradas.length === 0 ? (
          <div className="rounded-lg border border-dashed px-5 py-12 text-center" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
            Sin boletas de labores en el filtro actual.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="w-full border-collapse text-xs">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}><SortableTh sort={sort} sortKey="numero">Número</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="fecha">Día</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="finca">Lote</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="supervisor">Capturó</SortableTh></th>
                  <th className={`${thCls} text-right`}>Personas</th>
                  <th className={`${thCls} text-right`}>Horas</th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="costo" align="right">Costo</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="estado">Estado</SortableTh></th>
                  <th className={thCls}></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((b) => {
                  // Abierta = editable hasta el cierre semanal (como Jornadas de Planta).
                  const abierta = !b.cierre_semanal_id && b.estado !== 'anulada' && b.estado !== 'rechazada';
                  const r = resumenBoletas[b.id] ?? { personas: 0, horas: 0 };
                  return (
                    <tr key={b.id} className={trCls} style={{ cursor: 'pointer' }}
                      onClick={() => (abierta && puedeCapturarBoleta ? (setEditandoId(b.id), setCapturando(true)) : setVerBoletaId(b.id))}>
                      <td className={`${tdCls} font-mono font-bold`} style={{ color: 'var(--emp-accent-txt)' }}>{b.numero}</td>
                      <td className={tdCls}>{diaSem(b.fecha)} {fmtDate(b.fecha)}</td>
                      <td className={tdCls}>
                        <span className="font-semibold" style={{ color: 'var(--ink)' }}>Lote {b.lote_num || '-'}</span>
                        <span style={{ color: 'var(--ink-faint)' }}> · {b.finca_nombre || '-'}{b.estadio_codigo ? ` · ${b.estadio_codigo}` : ''}</span>
                      </td>
                      <td className={tdCls}>{b.supervisor_nombre || '-'}</td>
                      <td className={`${tdCls} text-right font-mono`}>{r.personas}</td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtHM(r.horas)}</td>
                      <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(b.costo_total)}</td>
                      <td className={tdCls}>
                        {b.cierre_semanal_id
                          ? <span className="rounded px-2 py-0.5 text-[11px] font-semibold" style={{ background: '#22c55e22', color: '#22c55e' }}>Cerrada</span>
                          : abierta
                            ? <span className="rounded px-2 py-0.5 text-[11px] font-semibold" style={{ background: '#38bdf822', color: '#38bdf8' }}>Abierta</span>
                            : <EstadoBadge estado={b.estado} />}
                      </td>
                      <td className={`${tdCls} text-right`} style={{ color: 'var(--ink-faint)' }}>
                        {abierta && puedeCapturarBoleta ? <Pencil size={14} className="inline" /> : <Eye size={14} className="inline" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {verBoletaId && (
        <BoletaDetalle
          empresaId={empresaId}
          boletaId={verBoletaId}
          permisos={permisos}
          onClose={() => setVerBoletaId('')}
          onChanged={onBoletaCambiada}
          onError={setError}
        />
      )}
    </div>
  );
}

export default BoletasLaboresPage;
